/**
 * MCP Tools for Opportunities Management
 * 
 * These tools allow MCP clients to interact with sales opportunities
 */

import { MCPTool } from '../types';
import { opportunitiesService } from '../../services/opportunitiesService';
import { Opportunity } from '../../types';
import { getMCPServer } from '../server/MCPServer';

// ============================================================================
// CREATE OPPORTUNITY
// ============================================================================

export const createOpportunityTool: MCPTool = {
  name: 'create_opportunity',
  description: 'Creates a new sales opportunity/deal. Use when user says: "Erstelle Opportunity", "Neuer Deal", "Neue Verkaufschance". Returns the created opportunity with ID.',
  
  inputSchema: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Opportunity title/name (required)'
      },
      companyName: {
        type: 'string',
        description: 'Company or customer name'
      },
      value: {
        type: 'number',
        description: 'Expected deal value in organization currency'
      },
      stage: {
        type: 'string',
        enum: ['qualification', 'proposal', 'negotiation', 'closed-won', 'closed-lost'],
        description: 'Current stage in sales pipeline'
      },
      probability: {
        type: 'number',
        description: 'Win probability percentage (0-100)'
      },
      expectedCloseDate: {
        type: 'string',
        description: 'Expected close date in ISO format (YYYY-MM-DD)'
      },
      description: {
        type: 'string',
        description: 'Detailed description of the opportunity'
      },
      leadIds: {
        type: 'array',
        items: { type: 'string' },
        description: 'Array of lead IDs associated with this opportunity'
      }
    },
    required: ['title', 'companyName', 'value']
  },

  async execute(params, context) {
    const opportunity: Omit<Opportunity, 'id'> = {
      title: params.title,
      companyName: params.companyName,
      value: params.value,
      stage: params.stage || 'qualification',
      probability: params.probability || 50,
      expectedCloseDate: params.expectedCloseDate ? new Date(params.expectedCloseDate) : new Date(),
      description: params.description || '',
      leadIds: params.leadIds || [],
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: context.userId,
      orgId: context.orgId,
      assignedTo: context.userId
    };

    const created = await opportunitiesService.createOpportunity(opportunity);

    return {
      success: true,
      opportunity: created,
      message: `Opportunity "${created.title}" created successfully with value €${created.value}`
    };
  }
};

// ============================================================================
// LIST OPPORTUNITIES
// ============================================================================

export const listOpportunitiesTool: MCPTool = {
  name: 'list_opportunities',
  description: 'Lists ALL opportunities for the organization. Use when user asks: "Welche Opportunities?", "Zeig Deals", "Was ist in der Pipeline?", "Verkaufschancen auflisten". Supports filtering by stage, value range. Returns array of opportunities.',
  
  inputSchema: {
    type: 'object',
    properties: {
      stage: {
        type: 'string',
        enum: ['qualification', 'proposal', 'negotiation', 'closed-won', 'closed-lost'],
        description: 'Filter by pipeline stage'
      },
      minValue: {
        type: 'number',
        description: 'Minimum opportunity value'
      },
      maxValue: {
        type: 'number',
        description: 'Maximum opportunity value'
      },
      limit: {
        type: 'number',
        description: 'Maximum number to return (default: 50)'
      }
    }
  },

  async execute(params, context) {
    let opportunities = await opportunitiesService.getOpportunities(context.userId, context.orgId);

    // Apply filters
    if (params.stage) {
      opportunities = opportunities.filter(o => o.stage === params.stage);
    }

    if (params.minValue !== undefined) {
      opportunities = opportunities.filter(o => o.value >= params.minValue);
    }

    if (params.maxValue !== undefined) {
      opportunities = opportunities.filter(o => o.value <= params.maxValue);
    }

    // Apply limit
    const limit = params.limit || 50;
    opportunities = opportunities.slice(0, limit);

    // Calculate total value
    const totalValue = opportunities.reduce((sum, o) => sum + (o.value || 0), 0);

    return {
      success: true,
      opportunities,
      count: opportunities.length,
      totalValue,
      hasMore: opportunities.length === limit
    };
  }
};

// ============================================================================
// GET OPPORTUNITY
// ============================================================================

export const getOpportunityTool: MCPTool = {
  name: 'get_opportunity',
  description: 'Retrieves a specific opportunity by ID. Use when user asks for details about a specific deal. Example: "Details zur Opportunity X", "Zeig mir Deal Y".',
  
  inputSchema: {
    type: 'object',
    properties: {
      opportunityId: {
        type: 'string',
        description: 'Unique identifier of the opportunity'
      }
    },
    required: ['opportunityId']
  },

  async execute(params, context) {
    const opportunity = await opportunitiesService.getOpportunity(params.opportunityId);

    if (!opportunity) {
      return {
        success: false,
        error: `Opportunity with ID ${params.opportunityId} not found`
      };
    }

    if (opportunity.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Opportunity belongs to different organization'
      };
    }

    return {
      success: true,
      opportunity
    };
  }
};

// ============================================================================
// UPDATE OPPORTUNITY
// ============================================================================

export const updateOpportunityTool: MCPTool = {
  name: 'update_opportunity',
  description: 'Updates an existing opportunity. Use when user says: "Verschiebe Deal in Negotiation", "Ändere Opportunity Wert", "Update Stage". Can update stage, value, probability, close date, etc.',
  
  inputSchema: {
    type: 'object',
    properties: {
      opportunityId: {
        type: 'string',
        description: 'Opportunity ID to update'
      },
      title: {
        type: 'string',
        description: 'Updated title'
      },
      stage: {
        type: 'string',
        enum: ['qualification', 'proposal', 'negotiation', 'closed-won', 'closed-lost'],
        description: 'Updated pipeline stage'
      },
      value: {
        type: 'number',
        description: 'Updated deal value'
      },
      probability: {
        type: 'number',
        description: 'Updated win probability (0-100)'
      },
      expectedCloseDate: {
        type: 'string',
        description: 'Updated expected close date (YYYY-MM-DD)'
      },
      description: {
        type: 'string',
        description: 'Updated description'
      }
    },
    required: ['opportunityId']
  },

  async execute(params, context) {
    const { opportunityId, ...updates } = params;

    // Verify opportunity exists and belongs to org
    const existing = await opportunitiesService.getOpportunity(opportunityId);
    
    if (!existing) {
      return {
        success: false,
        error: `Opportunity with ID ${opportunityId} not found`
      };
    }

    if (existing.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Opportunity belongs to different organization'
      };
    }

    // Process date if provided
    if (updates.expectedCloseDate) {
      updates.expectedCloseDate = new Date(updates.expectedCloseDate);
    }

    // Update the opportunity
    await opportunitiesService.updateOpportunity(opportunityId, {
      ...updates,
      updatedAt: new Date()
    });

    const updated = await opportunitiesService.getOpportunity(opportunityId);

    return {
      success: true,
      opportunity: updated,
      message: `Opportunity "${updated?.title}" updated successfully`
    };
  }
};

// ============================================================================
// SEARCH OPPORTUNITIES
// ============================================================================

export const searchOpportunitiesTool: MCPTool = {
  name: 'search_opportunities',
  description: 'Searches opportunities using query. Use when user asks: "Suche Deal", "Finde Opportunity für Firma X", "Welche Deals mit Microsoft?". Searches across title, company name, description. Returns matching opportunities.',
  
  inputSchema: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query (searches title, company, description)'
      },
      limit: {
        type: 'number',
        description: 'Maximum results (default: 20)'
      }
    },
    required: ['query']
  },

  async execute(params, context) {
    const opportunities = await opportunitiesService.getOpportunities(context.userId, context.orgId);
    const queryLower = params.query.toLowerCase();
    
    const matched = opportunities
      .filter(opp => {
        return (
          opp.title?.toLowerCase().includes(queryLower) ||
          opp.companyName?.toLowerCase().includes(queryLower) ||
          opp.description?.toLowerCase().includes(queryLower)
        );
      })
      .slice(0, params.limit || 20);

    const totalValue = matched.reduce((sum, o) => sum + (o.value || 0), 0);

    return {
      success: true,
      opportunities: matched,
      count: matched.length,
      totalValue,
      query: params.query
    };
  }
};

// ============================================================================
// ADD NOTE TO OPPORTUNITY
// ============================================================================

export const addNoteToOpportunityTool: MCPTool = {
  name: 'add_note_to_opportunity',
  description: 'Adds a note/comment to an opportunity. Use when user says: "Notiere bei Opportunity", "Schreib eine Notiz zu", "Merke dir bei Deal", "Dokumentiere bei Opportunity". Examples: "Kunde hat Grundinteresse", "Braucht mehr Infos zum Produkt", "Entscheider ist der CEO". Creates timestamped note with author tracking.',
  
  inputSchema: {
    type: 'object',
    properties: {
      opportunityId: {
        type: 'string',
        description: 'Opportunity ID to add note to'
      },
      content: {
        type: 'string',
        description: 'Note content (what to remember about this opportunity)'
      },
      type: {
        type: 'string',
        enum: ['general', 'strategy', 'concern', 'competitive'],
        description: 'Type of note (default: general)'
      }
    },
    required: ['opportunityId', 'content']
  },

  async execute(params, context) {
    const { opportunityId, content, type = 'general' } = params;

    // Verify opportunity exists and belongs to org
    const existing = await opportunitiesService.getOpportunity(opportunityId);
    
    if (!existing) {
      return {
        success: false,
        error: `Opportunity with ID ${opportunityId} not found`
      };
    }

    if (existing.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Opportunity belongs to different organization'
      };
    }

    // Add the note using existing service
    await opportunitiesService.addNoteToOpportunity(
      opportunityId,
      content,
      type as 'general' | 'strategy' | 'concern' | 'competitive',
      context.userId
    );

    return {
      success: true,
      message: `Note added to opportunity "${existing.title}"`,
      note: {
        content,
        type,
        opportunityId,
        createdBy: context.userId
      }
    };
  }
};

// ============================================================================
// REGISTER ALL OPPORTUNITY TOOLS
// ============================================================================

export function registerOpportunityTools(): void {
  const server = getMCPServer();
  
  server.registerTool(createOpportunityTool, {
    category: 'opportunities',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(listOpportunitiesTool, {
    category: 'opportunities',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getOpportunityTool, {
    category: 'opportunities',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(updateOpportunityTool, {
    category: 'opportunities',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(searchOpportunitiesTool, {
    category: 'opportunities',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(addNoteToOpportunityTool, {
    category: 'opportunities',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  console.log('✅ Registered 6 opportunity management tools');
}

